import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type { FlatfinderData } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TextArea } from '@/components/ui/text-area';
import { getSafeFlatfinderContactUrl } from './display';

function normalizeContactLink(value: string) {
  const contact = value.trim();
  if (/^[^\s@:/?#]+@[^\s@:/?#]+\.[^\s@:/?#]+$/.test(contact)) return `mailto:${contact}`;
  if (/^[^\s/:?#]+\.[^\s/:?#]+(?::\d+)?(?:[/?#][^\s]*)?$/.test(contact)) {
    return `https://${contact}`;
  }
  return contact;
}

export function FlatfinderSettings({
  data,
  busy,
  saveError,
  onSave,
}: {
  data: FlatfinderData;
  busy: boolean;
  saveError: string | null;
  onSave: (status: 'draft' | 'published', contact: string | null) => void;
}) {
  const { t } = useTranslation();
  const [contact, setContact] = useState(data.contactLink ?? '');
  const [height, setHeight] = useState('720');
  const [contactInvalid, setContactInvalid] = useState(false);

  function saveSettings(status: 'draft' | 'published') {
    const contactValue = normalizeContactLink(contact);
    if (contactValue && !getSafeFlatfinderContactUrl(contactValue)) {
      setContactInvalid(true);
      return;
    }
    setContactInvalid(false);
    setContact(contactValue);
    onSave(status, contactValue || null);
  }
  const publicUrl = data.publicId
    ? `${window.location.origin}/shared/flatfinder/${encodeURIComponent(data.publicId)}`
    : '';
  const parsedHeight = height.trim() ? Number(height) : 720;
  const embedHeight = Number.isFinite(parsedHeight)
    ? Math.max(320, Math.min(2000, parsedHeight))
    : 720;
  const embedCode = `<iframe src="${publicUrl}?embed=1" title="${t('flatfinderFormFlatfinder')}" width="100%" height="${embedHeight}" style="border:0" allow="fullscreen" allowfullscreen loading="lazy"></iframe>`;
  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('copied'));
    } catch {
      toast.error(t('copyFailed'));
    }
  }
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        saveSettings(data.status);
      }}
    >
      {data.project.status !== 'published' && (
        <p className="text-sm text-amber-800">{t('flatfinderFormPublishProjectFirst')}</p>
      )}
      <Input
        autoFocus
        label={t('flatfinderFormContactLabel')}
        value={contact}
        onChange={(event) => {
          setContact(event.target.value);
          setContactInvalid(false);
        }}
        aria-invalid={contactInvalid}
        errors={contactInvalid ? [t('flatfinderContactInvalid')] : undefined}
        disabled={!data.permissions.general || busy}
        placeholder={t('flatfinderFormContactPlaceholder')}
        wrapperClassName="min-w-48 flex-1"
      />
      {publicUrl && (
        <>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                {t('flatfinderFormOpenViewer')}
              </a>
            </Button>
            <Button type="button" onClick={() => copyText(publicUrl)} variant="outline">
              {t('copyPublicLink')}
            </Button>
          </div>
          <Input
            label={t('embedHeightPixels')}
            type="number"
            min={320}
            max={2000}
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            onBlur={() => setHeight(String(embedHeight))}
            wrapperClassName="w-32"
          />
          <TextArea
            label={t('embedCode')}
            readOnly
            value={embedCode}
            rows={3}
            className="min-h-24 font-mono text-xs"
          />
          <Button
            type="button"
            onClick={() => copyText(embedCode)}
            className="self-start"
            variant="outline"
          >
            {t('copyEmbedCode')}
          </Button>
        </>
      )}
      {saveError ? (
        <p role="alert" className="text-sm text-danger">
          {saveError}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
        <Button type="submit" disabled={!data.permissions.general || busy} variant="outline">
          {t('saveSettings')}
        </Button>
        <Button
          type="button"
          disabled={
            !data.permissions.general ||
            busy ||
            (data.status === 'draft' && data.project.status !== 'published')
          }
          onClick={() => saveSettings(data.status === 'published' ? 'draft' : 'published')}
          loading={busy}
        >
          {data.status === 'published' ? t('unpublish') : t('publish')}
        </Button>
      </div>
    </form>
  );
}
